import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetMonthlyOvertimeBalanceQuery,
  useGetOvertimeRequestsQuery,
} from "../../slices/overtimeApiSlice";
import { useGetCalendarByAcademyYearQuery } from "../../slices/calendarApiSlice";
import { useGetUserByEmailQuery } from "../../slices/userApiSlice";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  TrendingUp,
  RotateCw,
  ChevronLeft,
  RefreshCw,
  Edit3,
  Calendar as CalendarIcon,
  Filter,
} from "lucide-react";

// shadcn UI imports
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";

export default function OvertimeMonthlyLimit() {
  const navigate = useNavigate();
  const {
    data: balanceData,
    isLoading: isBalanceLoading,
    error: balanceError,
    refetch: refetchBalance,
  } = useGetMonthlyOvertimeBalanceQuery();

  const { data: overtimeData, isLoading: isOvertimeLoading } =
    useGetOvertimeRequestsQuery();

  const userInfo = useSelector((state) => state.auth.userInfo);
  const userEmail = userInfo?.username || userInfo?.email;
  const { data: userByEmail } = useGetUserByEmailQuery(userEmail, {
    skip: !userEmail,
  });

  const academyId =
    userByEmail?.user?.academyId || userInfo?.academy_id || userInfo?.academyId;
  const currentYear = new Date().getFullYear();

  const { data: calendarData, isLoading: calendarLoading } =
    useGetCalendarByAcademyYearQuery(
      {
        academy_id: academyId,
        year: currentYear,
      },
      {
        skip: !academyId,
      }
    );

  const [statusBreakdown, setStatusBreakdown] = useState({
    pending: 0,
    forwarded: 0,
    approved: 0,
    rejected: 0,
    reapply: 0,
    reapplied: 0,
  });

  const handleRefresh = () => {
    refetchBalance();
  };

  useEffect(() => {
    if (overtimeData && Array.isArray(overtimeData)) {
      const breakdown = {
        pending: 0,
        forwarded: 0,
        approved: 0,
        rejected: 0,
        reapply: 0,
        reapplied: 0,
      };

      overtimeData.forEach((item) => {
        const status = item.status?.toUpperCase() || "";
        const hours = item.details?.totalHours || 0;

        if (status === "PENDING") {
          breakdown.pending += hours;
        } else if (status === "FORWARDED") {
          breakdown.forwarded += hours;
        } else if (status === "APPROVED") {
          breakdown.approved += hours;
        } else if (status === "REJECTED") {
          breakdown.rejected += hours;
        } else if (status === "REAPPLY") {
          breakdown.reapply += hours;
        } else if (status === "REAPPLIED") {
          breakdown.reapplied += hours;
        }
      });

      setStatusBreakdown(breakdown);
    }
  }, [overtimeData]);

  if (isBalanceLoading || isOvertimeLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] w-full">
        <RotateCw className="h-10 w-10 animate-spin text-zinc-500 mb-4" />
        <p className="text-zinc-600 font-medium">Loading overtime balance...</p>
      </div>
    );
  }

  if (!calendarLoading && !calendarData?.calendar) {
    return (
      <div className="p-4 sm:py-6 sm:px-8 space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full h-10 w-10"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold text-zinc-800">Monthly Overtime Limit</h1>
        </div>

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Calendar Not Configured</AlertTitle>
          <AlertDescription>
            The academic calendar for your organization has not been set up yet. This is required to view your overtime balance.
            Please contact your administrator.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (balanceError || !balanceData) {
    return (
      <div className="p-4 sm:py-6 sm:px-8 space-y-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Balance</AlertTitle>
          <AlertDescription>
            {balanceError?.data?.message || balanceError?.message || "Failed to load overtime balance."}
          </AlertDescription>
          <Button
            variant="outline"
            onClick={handleRefresh}
            className="mt-4 gap-2"
          >
            <RotateCw className="h-4 w-4" />
            Try Again
          </Button>
        </Alert>
      </div>
    );
  }

  const monthlyLimit = balanceData?.monthlyLimit || 0;
  const approvedHours = balanceData?.approvedHours || 0;
  const remainingBalance = balanceData?.remainingBalance || 0;
  const currentMonth =
    balanceData?.currentMonth ||
    new Date().toLocaleString("en-US", { month: "long", year: "numeric" });

  const percentageUsed = (approvedHours / monthlyLimit) * 100;
  const isNearLimit = percentageUsed >= 80;
  const isExceeded = percentageUsed > 100;

  return (
    <div className="p-4 sm:py-6 sm:px-8 space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-9 w-9 rounded-full text-zinc-600 border-zinc-300 hover:bg-zinc-50 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-zinc-800 tracking-tight">
              Monthly Overtime Limit
            </h1>
            <p className="text-sm text-zinc-500 mt-0.5">{currentMonth}</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          className="bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50 gap-2 h-9"
        >
          <RotateCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Main Limit Card */}
      <Card className="border-zinc-200 shadow-sm overflow-hidden">
        <CardHeader className="border-b bg-zinc-50/50">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-zinc-500" />
            <div>
              <CardTitle className="text-lg font-bold text-zinc-800">Balance Summary</CardTitle>
              <CardDescription>Overall status for the current month</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            {/* Limit */}
            <div className="space-y-1">
              <p className="text-zinc-500 text-xs font-bold uppercase">Monthly Limit</p>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-3xl font-bold text-zinc-900">{monthlyLimit.toFixed(1)}</span>
                <span className="text-zinc-500 text-sm font-medium">hrs</span>
              </div>
            </div>

            {/* Total Used */}
            <div className="space-y-1 border-y sm:border-y-0 sm:border-x border-zinc-100 py-4 sm:py-0">
              <p className="text-zinc-500 text-xs font-bold uppercase">Total Used</p>
              <div className="flex items-baseline justify-center gap-1">
                <span className={`text-3xl font-bold ${isExceeded ? "text-red-600" : isNearLimit ? "text-amber-600" : "text-zinc-800"}`}>
                  {approvedHours.toFixed(1)}
                </span>
                <span className="text-zinc-500 text-sm font-medium">hrs</span>
              </div>
            </div>

            {/* Remaining */}
            <div className="space-y-1">
              <p className="text-zinc-500 text-xs font-bold uppercase">Remaining</p>
              <div className="flex items-baseline justify-center gap-1">
                <span className={`text-3xl font-bold ${remainingBalance > 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {Math.max(0, remainingBalance).toFixed(1)}
                </span>
                <span className="text-zinc-500 text-sm font-medium">hrs</span>
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-zinc-100">
            <div className="flex justify-between items-end mb-1">
              <div className="space-y-0.5">
                <p className="text-sm font-bold text-zinc-700">Utilization</p>
                <div className="flex items-center gap-2 text-zinc-500 text-xs">
                  <Clock className="h-3.5 w-3.5" />
                  {approvedHours.toFixed(1)} / {monthlyLimit.toFixed(1)} hours officially approved
                </div>
              </div>
              <span className={`text-2xl font-black tabular-nums ${isExceeded ? "text-red-500" : isNearLimit ? "text-amber-500" : "text-zinc-800"}`}>
                {percentageUsed.toFixed(1)}%
              </span>
            </div>

            <Progress
              value={Math.min(percentageUsed, 100)}
              className={`h-2.5 bg-zinc-100 ${isExceeded ? "[&>div]:bg-red-500" :
                  isNearLimit ? "[&>div]:bg-amber-500" :
                    "[&>div]:bg-zinc-800"
                }`}
            />

            <div className="mt-4">
              {isExceeded ? (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg border border-red-100 text-sm">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>Limit exceeded by {(percentageUsed - 100).toFixed(1)}%</span>
                </div>
              ) : isNearLimit ? (
                <div className="flex items-center gap-2 p-3 bg-amber-50 text-amber-700 rounded-lg border border-amber-100 text-sm">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>Approaching limit: {percentageUsed.toFixed(1)}% utilized</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-100 text-sm">
                  <CheckCircle className="h-4 w-4 shrink-0" />
                  <span>Within limit: {remainingBalance.toFixed(1)} hours remaining</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Breakdown Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 px-1">
          <Filter className="h-4 w-4 text-zinc-500" />
          <h2 className="text-lg font-bold text-zinc-800">Status Breakdown</h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <StatusItem cardTitle="Pending" value={statusBreakdown.pending} colorClass="text-amber-600" bgClass="bg-amber-50" borderColor="border-amber-100" />
          <StatusItem cardTitle="Needs Re-Apply" value={statusBreakdown.reapply} colorClass="text-orange-600" bgClass="bg-orange-50" borderColor="border-orange-100" />
          <StatusItem cardTitle="Re-Applied" value={statusBreakdown.reapplied} colorClass="text-cyan-600" bgClass="bg-cyan-50" borderColor="border-cyan-100" />
          <StatusItem cardTitle="Rejected" value={statusBreakdown.rejected} colorClass="text-red-600" bgClass="bg-red-50" borderColor="border-red-100" />
          <StatusItem cardTitle="Forwarded" value={statusBreakdown.forwarded} colorClass="text-zinc-600" bgClass="bg-zinc-50" borderColor="border-zinc-200" />
          <StatusItem cardTitle="Approved" value={statusBreakdown.approved} colorClass="text-emerald-600" bgClass="bg-emerald-50" borderColor="border-emerald-100" />
        </div>
      </div>

      {/* Info & Help Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-zinc-200 shadow-sm">
          <CardHeader className="pb-3 border-b bg-zinc-50/30">
            <CardTitle className="text-base font-bold text-zinc-800 flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              Workflow Guide
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <GuideStep num="1" title="Pending" desc="Initially submitted request" />
              <GuideStep num="2" title="Re-Apply" desc="Needs required edits" />
              <GuideStep num="3" title="Re-Applied" desc="Corrected submission" />
              <GuideStep num="4" title="Forwarded" desc="Sent to manager review" />
              <GuideStep num="5" title="Rejected" desc="Request not approved" />
              <GuideStep num="6" title="Approved" desc="Finalized and recorded" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 shadow-sm">
          <CardHeader className="pb-3 border-b bg-zinc-50/30">
            <CardTitle className="text-base font-bold text-zinc-800 flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Monthly Statistics
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-5">
            <div className="grid grid-cols-2 gap-4">
              <SimpleStat label="Daily Average" value={monthlyLimit / 30} suffix="hrs" />
              <SimpleStat label="Utilization %" value={percentageUsed.toFixed(0)} suffix="%" />
              <SimpleStat label="Approved Records" value={approvedHours > 0 ? "Official" : "None"} />
              <SimpleStat label="Current Month" value={currentMonth.split(" ")[0]} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Internal Helper Components
function StatusItem({ cardTitle, value, colorClass, bgClass, borderColor }) {
  return (
    <Card className={`border ${borderColor} shadow-none ${bgClass}`}>
      <CardHeader className="p-3 pb-0 space-y-0">
        <CardTitle className="text-[10px] uppercase font-bold text-zinc-500 tracking-wider font-sans">{cardTitle}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-1">
        <div className={`text-2xl font-bold ${colorClass} tabular-nums`}>{value.toFixed(1)}</div>
      </CardContent>
    </Card>
  );
}

function GuideStep({ num, title, desc }) {
  return (
    <div className="flex gap-3 items-start group">
      <div className="h-6 w-6 rounded-full bg-zinc-100 flex items-center justify-center text-[10px] font-bold text-zinc-600 flex-shrink-0 group-hover:bg-zinc-800 group-hover:text-white transition-colors">
        {num}
      </div>
      <div>
        <p className="text-sm font-bold text-zinc-800 leading-none">{title}</p>
        <p className="text-[11px] text-zinc-500 mt-1 leading-tight">{desc}</p>
      </div>
    </div>
  );
}

function SimpleStat({ label, value, suffix = "" }) {
  return (
    <div className="p-3 border border-zinc-100 rounded-lg bg-white">
      <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-tighter mb-1">{label}</p>
      <div className="flex items-baseline gap-0.5">
        <span className="text-lg font-bold text-zinc-800">{typeof value === 'number' ? value.toFixed(1) : value}</span>
        <span className="text-[10px] font-bold text-zinc-500">{suffix}</span>
      </div>
    </div>
  );
}
